import {useState, useCallback, useRef, useMemo} from 'react'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {debounce} from '@github/mini-throttle'

// empty and loading are client-side states, not returned by the server
export type TagState =
  | 'branch_exists'
  | 'duplicate'
  | 'empty'
  | 'immutable'
  | 'invalid'
  | 'loading'
  | 'pending'
  | 'valid'

interface TagValidationResult {
  state: TagState
  message: string
  validateTag: (tag: string) => void
}

function getValidationMessage(state: TagState, tagName: string) {
  switch (state) {
    case 'branch_exists':
    case 'pending':
    case 'valid':
      return `${tagName} will be created on publish`
    case 'duplicate':
      return `${tagName} is used by another release`
    case 'empty':
      return 'Tag cannot be blank'
    case 'immutable':
      return `${tagName} is used by an immutable release and cannot be reused`
    case 'invalid':
      return 'Contains invalid characters. Use safe special characters ., -, or _ instead'
    default:
      return ''
  }
}

export function useTagValidation(tagValidationUrl: string): TagValidationResult {
  const [state, setState] = useState<TagState>('empty')
  const [message, setMessage] = useState<string>('')
  const latestTagRef = useRef<string>('')

  const validateTag = useCallback(
    async (tag: string) => {
      latestTagRef.current = tag

      if (!tag) {
        setState('empty')
        setMessage('Tag cannot be blank')
        return
      }

      setState('loading')

      const validationUrl = new URL(tagValidationUrl, window.location.origin)
      const params = new URLSearchParams(validationUrl.search.slice(1))
      params.append('tag_name', tag)
      validationUrl.search = params.toString()

      try {
        const response = await verifiedFetch(validationUrl.toString(), {
          headers: {Accept: 'application/json'},
        })

        // Ignore if tag has changed
        if (tag !== latestTagRef.current) return

        if (!response.ok) {
          setState('invalid')
          setMessage('Unable to validate tag name. Please try again.')
          return
        }

        const data = await response.json()
        const tagState = data.status as TagState

        setState(tagState || 'empty')
        setMessage(getValidationMessage(tagState || 'empty', tag))
      } catch {
        if (tag !== latestTagRef.current) return

        setState('invalid')
        setMessage('Unable to validate tag name. Please try again.')
      }
    },
    [tagValidationUrl],
  )

  // eslint-disable-next-line react-hooks/refs
  const debouncedValidateTag = useMemo(() => debounce(validateTag, 500), [validateTag])

  return {state, message, validateTag: debouncedValidateTag}
}
